import { useLayoutEffect } from 'react';
import { Tour } from 'antd';
import useCheckPermission from 'hooks/useCheckPermission';
import { permissions } from 'constants/permissions';
import {
    useGetApiUserPreferencesParsedQuery,
    usePutApiUserPreferencesMutation,
} from 'store/userOrg/enhancedUserOrgApi';
import useConfig from 'hooks/useConfig';
import { ExtendedTourStepProps } from 'types/components';
import { IS_EFP } from 'config';

const getTargetWithScroll = (id: string) => {
    return () => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return element;
        }
        return document.body;
    };
};

const steps: ExtendedTourStepProps[] = [
    {
        title: 'Welcome',
        description: 'Welcome to the LEXIS Platform.',
        target: null,
        placement: 'center',
    },
    {
        title: 'Main menu',
        description: 'This is the main menu. Here you can see several items. I will tell more about them in next steps',
        placement: 'bottomRight',
        target: getTargetWithScroll('menu'),
    },
    {
        title: 'Dashboard',
        description: 'Here you can find main information about your projects, workflows and datasets',
        placement: 'bottomRight',
        target: getTargetWithScroll('dashboard'),
    },
    {
        title: 'Data Management',
        description: 'Here you can upload and see different types of data',
        placement: 'bottomRight',
        target: getTargetWithScroll('data-management'),
    },
    {
        title: 'Workflows',
        description: 'You can create your Workflow and check your workflow execution',
        placement: 'bottomRight',
        target: getTargetWithScroll('workflows'),
    },
    {
        title: 'Projects',
        description: 'You can create your project or request project access',
        placement: 'bottomRight',
        target: getTargetWithScroll('projects'),
    },
    {
        permission: permissions.locList,
        title: 'Locations',
        description: 'You can manage your locations',
        placement: 'bottomRight',
        target: getTargetWithScroll('management-locations'),
    },
    {
        permission: permissions.resList,
        title: 'Resource managment',
        description: 'You can manage your resources and assignments',
        placement: 'bottomRight',
        target: getTargetWithScroll('resources'),
    },
    {
        title: 'Documentation',
        description: 'You can check documentation for more information',
        placement: 'bottomRight',
        target: getTargetWithScroll('documentation'),
    },
    {
        title: 'Project filter',
        description: 'You can check your project filter notifications',
        placement: 'bottom',
        target: getTargetWithScroll('global-filter-notifications'),
    },
    {
        title: 'Workflow Executions',
        description: 'You can check your workflow executions notifications',
        placement: 'bottom',
        target: getTargetWithScroll('workflow-execution-notifications'),
    },
    {
        title: 'Project Access Requests',
        description: 'You can check project access requests notifications',
        placement: 'bottom',
        target: getTargetWithScroll('access-request-notifications'),
    },
    {
        title: 'User Settings',
        description: 'Here you can find information about the user and start this guide again',
        placement: 'bottom',
        target: getTargetWithScroll('user-settings'),
    },
];

const BeginTour = () => {
    const { firstStart, showGuide, onChangeShowGuide } = useConfig();

    const [putPreferences] = usePutApiUserPreferencesMutation();
    const { data: preferences } = useGetApiUserPreferencesParsedQuery();

    const checkPerms = useCheckPermission();

    const filteredSteps = steps?.filter(step => {
        if (IS_EFP && (step.title === 'Projects' || step.title === 'Project Access Requests')) {
            return false;
        }
        return step.permission ? checkPerms(step.permission, '') : step;
    });

    useLayoutEffect(() => {
        !firstStart ? onChangeShowGuide(true) : onChangeShowGuide(false);
    }, [firstStart]);

    const changeShowGuide = () =>
        !preferences?.firstStart &&
        putPreferences({
            body: {
                ...preferences,
                firstStart: true,
            },
        });

    return (
        <>
            <Tour
                open={showGuide}
                onClose={() => {
                    onChangeShowGuide(false);
                    changeShowGuide();
                }}
                steps={filteredSteps}
                onFinish={() => changeShowGuide()}
                scrollIntoViewOptions={{ block: 'center' }}
                zIndex={2000}
            />
        </>
    );
};

export default BeginTour;
